// src/components/UserProfileModal.tsx
import { logger } from '../utils/logger';
// Modal for viewing other users' profiles with actions (block, report, connect)

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonCard,
  IonCardContent,
  IonChip,
  IonSpinner,
  IonText,
  IonList,
  IonItem,
  IonLabel,
  IonActionSheet,
  IonAlert,
} from '@ionic/react';
import {
  close,
  locationOutline,
  gameController,
  ellipsisHorizontal,
  person,
  personAdd,
  banOutline,
  flagOutline,
  chatbubbleOutline,
  timeOutline,
} from 'ionicons/icons';
import { AvatarPreview } from './avatar/AvatarPreview';
import { profileService } from '../services/profileService';
import { supabase } from '../services/supabase';
import { Profile, Availability } from '../types/profile';
import { UserGame } from '../types/games';
import { ReportModal } from './ReportModal';
import { sendConnectionRequest, getConnectionStatus, blockUser } from '../services/connectionsService';
import { useHistory } from 'react-router-dom';
import './UserProfileModal.css';

interface UserProfileModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  userId: string;
  currentUserId: string;
  onBlock?: () => void;
  onConnect?: () => void;
  showChatButton?: boolean;
  onChatClick?: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onDismiss,
  userId,
  currentUserId,
  onBlock,
  onConnect,
  showChatButton = false,
  onChatClick,
}) => {
  const history = useHistory();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [games, setGames] = useState<UserGame[]>([]);
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(true);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockAlert, setShowBlockAlert] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'none' | 'pending' | 'connected'>('none');

  useEffect(() => {
    if (isOpen && userId) {
      loadProfile();
      checkConnectionStatus();
    }
  }, [isOpen, userId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const profileData = await profileService.getProfileById(userId);
      setProfile(profileData);

      // Load user's games and availability in parallel
      const [gamesResult, availabilityResult] = await Promise.all([
        supabase
          .from('user_games')
          .select(`
            id,
            user_id,
            game_id,
            ownership_status,
            ttrpg_preference,
            created_at,
            games:game_id (
              id,
              name,
              game_type,
              min_players,
              max_players,
              year_published
            )
          `)
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('availability')
          .select('*')
          .eq('user_id', userId)
          .order('day_of_week', { ascending: true })
          .order('start_time', { ascending: true })
      ]);

      if (!gamesResult.error && gamesResult.data) {
        const formattedGames = gamesResult.data.map((item: any) => {
          const gameData = Array.isArray(item.games) ? item.games[0] : item.games;
          return {
            id: item.id,
            user_id: item.user_id,
            game_id: item.game_id,
            name: gameData?.name || 'Unknown Game',
            game_type: gameData?.game_type,
            ownership_status: item.ownership_status,
            ttrpg_preference: item.ttrpg_preference,
            min_players: gameData?.min_players,
            max_players: gameData?.max_players,
            year_published: gameData?.year_published,
            created_at: item.created_at,
          };
        });
        setGames(formattedGames);
      }

      if (!availabilityResult.error && availabilityResult.data) {
        setAvailability(availabilityResult.data);
      }
    } catch (error) {
      logger.error('Error loading profile:', error);
    }
    setLoading(false);
  };

  const checkConnectionStatus = async () => {
    try {
      const status = await getConnectionStatus(userId);
      setConnectionStatus(status);
    } catch (error) {
      logger.error('Error checking connection status:', error);
    }
  };

  const handleBlock = async () => {
    setShowBlockAlert(false);
    try {
      await blockUser(userId);
      if (onBlock) onBlock();
      onDismiss();
    } catch (error) {
      logger.error('Error blocking user:', error);
      alert('Failed to block user. Please try again.');
    }
  };

  const handleConnect = async () => {
    try {
      await sendConnectionRequest(userId);
      setConnectionStatus('pending');
      if (onConnect) onConnect();
      alert('Connection request sent!');
    } catch (error) {
      logger.error('Error sending connection request:', error);
      alert('Failed to send connection request. Please try again.');
    }
  };

  const handleReportComplete = () => {
    setShowReportModal(false);
    onDismiss();
  };

  const getPlayPreferenceChips = () => {
    if (!profile?.play_preferences) return null;

    const prefs = profile.play_preferences as string[];
    return (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
        {prefs.includes('in-person') && (
          <IonChip color="primary" style={{ fontSize: '12px' }}>
            <IonIcon icon={locationOutline} />
            <IonLabel>In-Person</IonLabel>
          </IonChip>
        )}
        {prefs.includes('online') && (
          <IonChip color="secondary" style={{ fontSize: '12px' }}>
            <IonIcon icon={gameController} />
            <IonLabel>Online</IonLabel>
          </IonChip>
        )}
      </div>
    );
  };

  const renderGames = () => {
    if (games.length === 0) {
      return (
        <IonText color="medium" style={{ fontSize: '14px' }}>
          <p>No games listed</p>
        </IonText>
      );
    }

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
        {games.slice(0, 10).map((game) => (
          <IonChip key={game.id} color="light" style={{ fontSize: '12px' }}>
            {game.name}
          </IonChip>
        ))}
        {games.length > 10 && (
          <IonText color="medium" style={{ fontSize: '12px', marginTop: '4px' }}>
            +{games.length - 10} more
          </IonText>
        )}
      </div>
    );
  };

  const getDayName = (dayNum: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNum] || 'Unknown';
  };

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const renderAvailability = () => {
    if (availability.length === 0) {
      return (
        <IonText color="medium" style={{ fontSize: '14px' }}>
          <p>No availability set</p>
        </IonText>
      );
    }

    // Group by day
    const byDay: { [key: number]: Availability[] } = {};
    availability.forEach(slot => {
      if (!byDay[slot.day_of_week]) {
        byDay[slot.day_of_week] = [];
      }
      byDay[slot.day_of_week].push(slot);
    });

    return (
      <div style={{ marginTop: '8px' }}>
        {Object.keys(byDay).sort((a, b) => parseInt(a) - parseInt(b)).map(dayNum => {
          const day = parseInt(dayNum);
          const slots = byDay[day];
          return (
            <div key={day} style={{ marginBottom: '12px' }}>
              <IonText color="medium" style={{ fontSize: '12px', fontWeight: '600' }}>
                {getDayName(day)}
              </IonText>
              <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {slots.map((slot, idx) => (
                  <IonChip key={idx} color="tertiary" style={{ fontSize: '12px' }}>
                    {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                  </IonChip>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onDismiss} className="user-profile-modal">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onDismiss}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
            <IonTitle>{profile?.display_name || 'Profile'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowActionSheet(true)}>
                <IonIcon icon={ellipsisHorizontal} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding user-profile-modal-content">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
              <IonSpinner />
            </div>
          ) : profile ? (
            <>
              {/* Avatar */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <AvatarPreview avatarData={profile.avatar} size={120} userId={profile.id} isAdmin={profile.is_admin || false} />
                <h2 style={{ marginTop: '16px', marginBottom: '4px' }}>
                  {profile.display_name}
                </h2>
                {profile.pronouns && (
                  <IonText color="medium">
                    <p style={{ fontSize: '14px', margin: '0 0 8px 0' }}>{profile.pronouns}</p>
                  </IonText>
                )}
                {profile.is_admin && (
                  <IonChip color="danger" style={{ fontSize: '12px' }}>
                    <IonLabel>Admin</IonLabel>
                  </IonChip>
                )}
              </div>

              {/* Location */}
              {profile.location_city && (
                <IonCard>
                  <IonCardContent>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IonIcon icon={locationOutline} color="medium" />
                      <IonText color="medium">
                        <strong>{profile.location_city}</strong>
                      </IonText>
                    </div>
                  </IonCardContent>
                </IonCard>
              )}

              {/* Bio */}
              {profile.bio && (
                <IonCard>
                  <IonCardContent>
                    <h3 style={{ marginTop: 0, fontSize: '16px' }}>About</h3>
                    <IonText>
                      <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{profile.bio}</p>
                    </IonText>
                  </IonCardContent>
                </IonCard>
              )}

              {/* Play Preferences */}
              <IonCard>
                <IonCardContent>
                  <h3 style={{ marginTop: 0, fontSize: '16px' }}>Play Style</h3>
                  {getPlayPreferenceChips()}
                  {profile.online_platforms && (profile.online_platforms as string[]).length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <IonText color="medium" style={{ fontSize: '12px' }}>
                        <strong>Online Platforms:</strong>
                      </IonText>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                        {(profile.online_platforms as string[]).map((platform) => (
                          <IonChip key={platform} color="tertiary" style={{ fontSize: '12px', fontWeight: '500' }}>
                            {platform}
                          </IonChip>
                        ))}
                      </div>
                    </div>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Games */}
              <IonCard>
                <IonCardContent>
                  <h3 style={{ marginTop: 0, fontSize: '16px' }}>
                    Games ({games.length})
                  </h3>
                  {renderGames()}
                </IonCardContent>
              </IonCard>

              {/* Availability */}
              <IonCard>
                <IonCardContent>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <IonIcon icon={timeOutline} color="medium" />
                    <h3 style={{ margin: 0, fontSize: '16px' }}>
                      Availability
                    </h3>
                  </div>
                  {renderAvailability()}
                </IonCardContent>
              </IonCard>

              {/* Action Buttons */}
              <div className="user-profile-action-buttons">
                {showChatButton && onChatClick && (
                  <IonButton expand="block" onClick={onChatClick}>
                    <IonIcon icon={chatbubbleOutline} slot="start" />
                    Message
                  </IonButton>
                )}

                {connectionStatus === 'none' && (
                  <IonButton expand="block" color="primary" onClick={handleConnect}>
                    <IonIcon icon={personAdd} slot="start" />
                    Connect
                  </IonButton>
                )}

                {connectionStatus === 'pending' && (
                  <IonButton expand="block" color="medium" disabled>
                    <IonIcon icon={person} slot="start" />
                    Request Pending
                  </IonButton>
                )}

                {connectionStatus === 'connected' && (
                  <IonButton expand="block" color="success" disabled>
                    <IonIcon icon={person} slot="start" />
                    Connected
                  </IonButton>
                )}
              </div>
            </>
          ) : (
            <IonText color="danger">
              <p>Failed to load profile</p>
            </IonText>
          )}
        </IonContent>
      </IonModal>

      {/* Action Sheet */}
      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        header="Profile Actions"
        buttons={[
          {
            text: 'Report User',
            icon: flagOutline,
            handler: () => setShowReportModal(true),
          },
          {
            text: 'Block User',
            icon: banOutline,
            role: 'destructive',
            handler: () => setShowBlockAlert(true),
          },
          {
            text: 'Cancel',
            role: 'cancel',
          },
        ]}
      />

      {/* Report Modal */}
      {profile && (
        <ReportModal
          isOpen={showReportModal}
          onDismiss={() => setShowReportModal(false)}
          reportType="user"
          reportedId={userId}
          reportedName={profile.display_name || profile.username}
          onReportComplete={handleReportComplete}
        />
      )}

      {/* Block Alert */}
      <IonAlert
        isOpen={showBlockAlert}
        onDidDismiss={() => setShowBlockAlert(false)}
        header="Block User"
        message={`Are you sure you want to block ${profile?.display_name || 'this user'}? They will not be able to contact you.`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Block',
            role: 'destructive',
            handler: handleBlock,
          },
        ]}
      />
    </>
  );
};